import axios from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { ICurrency } from 'types';
import { getRate } from 'services/firebase/rates';

interface UseCurrencyRateParams {
  buyCurrency?: ICurrency['code'];
  sellCurrency?: ICurrency['code'];
  predicate?: boolean;
  withCancel?: boolean;
}

export interface UseCurrencyRateReturnValues {
  rate: number | null;
  isLoading: boolean;
  isError: boolean;
  conversionFeeRate: number;
  getRate: (
    sellCurrency: ICurrency['code'],
    buyCurrency: ICurrency['code'],
    cancelSimilarRequests?: boolean
  ) => Promise<number | undefined>;
}

const useCurrencyRate = ({
  buyCurrency,
  sellCurrency,
  predicate = true,
  withCancel = true,
}: UseCurrencyRateParams): UseCurrencyRateReturnValues => {
  const [rate, setRate] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [conversionFeeRate, setConversionFeeRate] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const getRateHandler = useCallback(
    async (
      sellCurrencyCode: ICurrency['code'],
      buyCurrencyCode: ICurrency['code'],
      cancelSimilarRequests = true
    ) => {
      try {
        setRate(null);
        setConversionFeeRate(0);
        setIsLoading(true);

        const rateResponse = await getRate({
          sellCurrency: sellCurrencyCode,
          buyCurrency: buyCurrencyCode,
          withCancel: cancelSimilarRequests,
        });
        /* 
          TODO: remove this condition when we will start returning 
          correct status codes etc. from the server
        */
        if (rateResponse?.success && rateResponse.data) {
          setRate(rateResponse.data.rate);
          setConversionFeeRate(rateResponse.data.conversionFeeRate);

          setIsError(false);
          return rateResponse.data.rate;
        } else {
          setIsError(true);
          return undefined;
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.warn('getRate request was canceled');
        } else {
          setIsError(true);
        }

        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (predicate && buyCurrency && sellCurrency) {
      getRateHandler(sellCurrency, buyCurrency, withCancel);
    }
  }, [buyCurrency, sellCurrency, getRateHandler, predicate, withCancel]);

  return {
    rate,
    isLoading,
    isError,
    conversionFeeRate,
    getRate: getRateHandler,
  };
};

export default useCurrencyRate;
